<template>
	<MultiselectDesktop
		v-if="!isMobile"
		v-model="internalValue"
		:options="props.options"
		:mode="props.mode"
		:max-height="props.maxHeight"
		:placeholder="props.placeholder"
		:disabled="props.disabled"
		:searchable="props.searchable"
		:can-clear="props.canClear"
		:no-options-text="props.noOptionsText"
		:no-results-text="props.noResultsText"
		:description="props.description"
		:warning="props.warning"
		@open="emit('open')"
		@close="emit('close')"
	/>
	<MultiselectProxyInput
		v-else
		:model-value="internalValue"
		:options="props.options"
		:mode="props.mode"
		:placeholder="props.placeholder"
		:disabled="props.disabled"
		:can-clear="props.canClear"
		:description="props.description"
		:warning="props.warning"
		:is-location-filter="props.isLocationFilter"
		@click="openMultiselectModal"
		@clear="handleClear"
		@remove-item="handleRemoveItem"
	/>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useMobile } from '@composables/useMobile';
import { useMobileModal } from '@composables/useMobileModal';
import MultiselectDesktop from '@components/Multiselect/MultiselectDesktop.vue';
import MultiselectMobile from '@components/Multiselect/MultiselectMobile.vue';
import MultiselectProxyInput from '@components/Multiselect/MultiselectProxyInput.vue';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';

const props = defineProps(multiselectPropsDefinition);

const emit = defineEmits<{
	'updateValues': [value: string | number | (string | number)[] | null];
	'open': [];
	'close': [];
	'changeContinent': [];
}>();

const { isMobile } = useMobile();
const { open: openMobileModal, close: closeMobileModal } = useMobileModal();

const internalValue = ref(props.modelValue);

const handleMobileUpdate = (value: string | number | (string | number)[] | null): void => {
	internalValue.value = value;
};

const handleMobileClose = (): void => {
	closeMobileModal();
	emit('close');
};

const openMultiselectModal = (): void => {
	if (props.disabled) return;

	emit('open');
	openMobileModal(MultiselectMobile, {
		modelValue: internalValue.value,
		options: props.options,
		mode: props.mode,
		maxHeight: props.maxHeight,
		placeholder: props.placeholder,
		disabled: props.disabled,
		searchable: props.searchable,
		canClear: props.canClear,
		noOptionsText: props.noOptionsText,
		noResultsText: props.noResultsText,
		description: props.description,
		warning: props.warning,
		isLocationFilter: props.isLocationFilter,
		onUpdateValues: handleMobileUpdate,
		onClose: handleMobileClose,
		onChangeContinent: () => emit('changeContinent')
	});
};

const handleClear = (): void => {
	internalValue.value = null;
};

const handleRemoveItem = (value: string | number): void => {
	if (Array.isArray(internalValue.value)) {
		internalValue.value = internalValue.value.filter((v: string | number) => v !== value);
	} else if (internalValue.value === value) {
		internalValue.value = null;
	}
};

watch(() => props.modelValue, (val) => {
	internalValue.value = val;
}, { deep: true });

watch(internalValue, (val) => {
	emit('updateValues', val);
}, { deep: true });
</script>
